import { useEffect, useRef, useState, type FormEvent } from "react";
import { listAllEvents, type AgendaMark } from "./agenda";
import { blankCard, emptyBoard, enqueueBoardWrite, freshId, parseBoard, plainSuivi, renderBoard, suiviDirty, todayStamp, type BoardFile } from "./atelier";
import { Atelier, CoffreCreateButtons, type AtelierStart } from "./Atelier";
import { CoffreBlock } from "./CoffreBlock";
import { createCoffreMarkdown, readCoffreText, saveCoffreText, uploadCoffreFile } from "./coffre";
import { FicheMark } from "./FicheMark";
import { NoteBlock } from "./NoteBlock";
import { DateField } from "./pickers";
import { relocatePath, renameFicheHome } from "./crmHome";
import { Modal } from "./Modal";
import { PlanningLink } from "./PlanningLink";
import { saveFiche } from "./api";
import { FicheShareLine } from "./share";
import { createTask } from "./tasks";
import type { CrmFiche } from "./types";

const PIPE = "pipeline.board.md";
const WEEK = ["L", "M", "M", "J", "V", "S", "D"];

export function InterneDesk({ fiche, onShare, naming = false, onNaming }: { fiche: CrmFiche; onShare: (fiche: CrmFiche) => void; naming?: boolean; onNaming?: (open: boolean) => void }) {
  const [atelier, setAtelier] = useState<AtelierStart | null>(null);
  const coffreOn = fiche.coffre_path !== "";

  return (
    <div className="crm-rise grid gap-3 px-6 pb-10 pt-5">
      <header className="flex flex-wrap items-start gap-4">
        <FicheMark path={fiche.logo_path} name={fiche.name} bare className="h-14 w-14 shrink-0 text-base" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Fiche interne</p>
          <div className="flex min-w-0 items-center gap-1">
            <h1 className="crm-title min-w-0 truncate text-2xl font-semibold tracking-tight">{fiche.name}</h1>
            {fiche.mine ? (
              <button type="button" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-current opacity-70 hover:opacity-100" aria-label={`Modifier ${fiche.name}`} title={`Modifier ${fiche.name}`} onClick={() => onNaming?.(true)}>
                <PencilIcon />
              </button>
            ) : null}
          </div>
          <p className="truncate text-sm text-slate-500">Coffre · {fiche.coffre_path}</p>
        </div>
        <div className="ml-auto min-w-[16rem] max-w-sm">
          <FicheShareLine fiche={fiche} onChange={onShare} />
        </div>
      </header>
      <NoteBlock key={fiche.id} folder={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} ficheId={fiche.id} origin={{ id: fiche.id, name: fiche.name, logo: fiche.logo_path }} />
      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section className="crm-card flex h-[26.5rem] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
          <h2 className="crm-head flex shrink-0 items-baseline gap-2 border-b px-4 py-2 text-sm font-semibold">
            <span>Coffre</span>
            <span className="min-w-0 truncate text-xs font-medium opacity-70">{fiche.coffre_path}</span>
            <span className="ml-auto"><CoffreCreateButtons disabled={!coffreOn} onCreate={setAtelier} /></span>
          </h2>
          <div className="min-h-0 flex-1 overflow-hidden p-4">
            {coffreOn ? <CoffreBlock home={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} fiche={fiche} /> : <p className="text-sm text-slate-500">Le dossier n’est pas prêt.</p>}
          </div>
        </section>
        <TeamAgenda />
      </div>
      <Pipe fiche={fiche} />
      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <AppDoor title="Chat" detail="La messagerie de l’équipe, pour cette fiche." href="https://chat.app.aureliasuite.fr/?aurelia_hub=2" />
        <PlanningLink fiche={fiche} onChange={onShare} />
      </div>
      {atelier && coffreOn ? <Atelier key={atelier} fiche={fiche} start={atelier} onClose={() => setAtelier(null)} /> : null}
      {naming ? <NameSheet fiche={fiche} onChange={onShare} onClose={() => onNaming?.(false)} /> : null}
    </div>
  );
}

function NameSheet({ fiche, onChange, onClose }: { fiche: CrmFiche; onChange: (fiche: CrmFiche) => void; onClose: () => void }) {
  const [name, setName] = useState(fiche.name);
  const [busy, setBusy] = useState(false);
  const [fault, setFault] = useState("");

  async function save(event: FormEvent) {
    event.preventDefault();
    const nextName = name.trim();
    if (!nextName || busy) return;
    setBusy(true);
    setFault("");
    try {
      let next = fiche;
      if (nextName !== fiche.name) {
        const home = await renameFicheHome(fiche.coffre_path, nextName);
        next = { ...next, name: nextName, coffre_path: home, logo_path: relocatePath(next.logo_path, fiche.coffre_path, home) };
      }
      onChange(await saveFiche(next));
      onClose();
    } catch (reason: unknown) {
      setFault(reason instanceof Error ? reason.message : "Enregistrement impossible");
      setBusy(false);
    }
  }

  async function onLogo(file: File) {
    setFault("");
    if (!fiche.coffre_path) {
      setFault("Le dossier de la fiche n’est pas prêt.");
      return;
    }
    setBusy(true);
    try {
      const logoPath = await uploadCoffreFile(fiche.coffre_path, logoFile(file), fiche.coffre_owner_id || 0, true);
      onChange(await saveFiche({ ...fiche, logo_path: logoPath }));
    } catch (reason: unknown) {
      setFault(reason instanceof Error ? reason.message : "Image non envoyée");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} closeDisabled={busy} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <form className="grid gap-4" onSubmit={(event) => void save(event)}>
          <div className="flex items-center gap-3">
            <FicheMark path={fiche.logo_path} name={name || fiche.name} bare className="h-14 w-14 text-base" />
            <label className="cursor-pointer text-sm font-semibold">
              Téléverser
              <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; if (file) void onLogo(file); event.target.value = ""; }} />
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">Nom</span>
            <input className="crm-field w-full rounded-xl border px-3 py-2" value={name} onChange={(event) => setName(event.target.value)} required />
          </label>
          {fault ? <p className="text-sm text-rose-700">{fault}</p> : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-full px-3 py-1.5 text-xs font-semibold" onClick={close}>Annuler</button>
            <button type="submit" className="crm-fill rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function logoFile(file: File): File {
  const raw = file.name.split(".").pop()?.toLowerCase() || "";
  const known = raw === "jpeg" ? "jpg" : raw;
  const ext = /^(png|jpg|gif|webp|svg|bmp|avif)$/.test(known) ? known : "jpg";
  return new File([file], `logo-crm.${ext}`, { type: file.type || "image/jpeg" });
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10.2-10.2a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4Z" />
    </svg>
  );
}

function TeamAgenda() {
  const [marks, setMarks] = useState<AgendaMark[]>([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [picked, setPicked] = useState(todayStamp);
  const month = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const dayEvents = marks.filter((item) => item.day === picked).sort((a, b) => a.time.localeCompare(b.time));

  useEffect(() => {
    let alive = true;
    void listAllEvents().then((rows) => { if (alive) setMarks(rows); }).catch(() => { if (alive) setMarks([]); });
    return () => { alive = false; };
  }, []);

  return (
    <section className="crm-card flex h-[26.5rem] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
      <h2 className="crm-head flex shrink-0 items-baseline gap-2 border-b px-4 py-2 text-sm font-semibold">
        <span>Agenda</span>
        <span className="text-xs font-medium opacity-70">{picked ? dayTitle(picked) : ""}</span>
      </h2>
      <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-2 p-3">
        <div>
          <div className="mb-1 flex items-center justify-between">
            <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Mois précédent" onClick={() => setCursor(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button>
            <p className="text-sm font-semibold capitalize">{month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</p>
            <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Mois suivant" onClick={() => setCursor(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button>
          </div>
          <MonthGrid month={month} marks={marks} picked={picked} onPick={setPicked} />
        </div>
        <ul className="crm-scroll min-h-0 space-y-2 overflow-auto">
          {dayEvents.length === 0 ? <li className="text-sm text-slate-500">Rien ce jour.</li> : null}
          {dayEvents.map((item) => (
            <li key={item.id} className="flex items-start gap-2 rounded-xl border border-slate-200 px-3 py-2">
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.color }} />
              <span className="min-w-0">
                <span className="block text-xs font-semibold">{item.time || "Journée"}</span>
                <span className="block truncate text-sm">{item.title}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function MonthGrid({ month, marks, picked, onPick }: { month: Date; marks: AgendaMark[]; picked: string; onPick: (day: string) => void }) {
  const year = month.getFullYear();
  const index = month.getMonth();
  const today = new Date();
  const lead = (new Date(year, index, 1).getDay() + 6) % 7;
  const days = new Date(year, index + 1, 0).getDate();
  const cells = [...Array(lead).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
      {WEEK.map((label, i) => <b key={`${label}-${i}`} className="crm-ink py-1 text-[11px] font-semibold uppercase">{label}</b>)}
      {cells.map((day, i) => {
        const stamp = day > 0 ? `${year}-${String(index + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";
        const on = stamp !== "" && today.getFullYear() === year && today.getMonth() === index && today.getDate() === day;
        const chosen = stamp !== "" && stamp === picked;
        const dot = marks.some((item) => item.day === stamp);
        return (
          <button key={i} type="button" disabled={!stamp} className="relative grid h-8 place-items-center disabled:cursor-default" onClick={() => stamp && onPick(stamp)}>
            <span className={chosen ? "crm-soft grid h-7 w-7 place-items-center rounded-full font-semibold" : on ? "crm-fill grid h-7 w-7 place-items-center rounded-full font-semibold text-white" : "grid h-7 w-7 place-items-center text-slate-700"}>
              {day || ""}
            </span>
            {dot ? <span className="absolute bottom-0 h-1 w-1 rounded-full bg-slate-400" /> : null}
          </button>
        );
      })}
    </div>
  );
}

function Pipe({ fiche }: { fiche: CrmFiche }) {
  const [board, setBoard] = useState<BoardFile | null>(null);
  const [error, setError] = useState("");
  const [view, setView] = useState<"kanban" | "liste">(() => (localStorage.getItem(`crm-interne-view-${fiche.id}`) === "liste" ? "liste" : "kanban"));
  const [draft, setDraft] = useState("");
  const path = `${fiche.coffre_path}/${PIPE}`;
  const ownerId = fiche.coffre_owner_id || 0;
  const hold = useRef(false);
  const session = useRef(0);
  const live = useRef<BoardFile | null>(null);
  live.current = board;

  useEffect(() => {
    const open = () => {
      session.current += 1;
      hold.current = true;
    };
    const shut = () => {
      const mine = session.current;
      hold.current = true;
      void readCoffreText(path, ownerId)
        .then((raw) => {
          if (mine !== session.current) return;
          const next = plainSuivi(parseBoard(raw));
          live.current = next;
          setBoard(next);
        })
        .catch(() => undefined)
        .finally(() => {
          if (mine === session.current) hold.current = false;
        });
    };
    window.addEventListener("crm-board-opened", open);
    window.addEventListener("crm-board-closed", shut);
    return () => {
      window.removeEventListener("crm-board-opened", open);
      window.removeEventListener("crm-board-closed", shut);
    };
  }, [path, ownerId]);

  useEffect(() => {
    let alive = true;
    setBoard(null);
    setError("");
    void readCoffreText(path, ownerId)
      .then((raw) => {
        if (!alive) return;
        const parsed = parseBoard(raw);
        const plain = plainSuivi(parsed);
        live.current = plain;
        setBoard(plain);
        if (suiviDirty(parsed)) {
          void enqueueBoardWrite(async () => {
            const current = live.current;
            if (!current || hold.current) return;
            await saveCoffreText(path, renderBoard(plainSuivi(current)), ownerId);
          }).catch(() => undefined);
        }
      })
      .catch(() => {
        const next = freshPipe();
        void createCoffreMarkdown(fiche.coffre_path, "pipeline.board", renderBoard(next), ownerId)
          .then(() => { if (alive) setBoard(next); })
          .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Le suivi n’a pas pu être créé."); });
      });
    return () => { alive = false; };
  }, [fiche.id, fiche.coffre_path, path, ownerId]);

  function paint(next: BoardFile) {
    if (hold.current) return;
    const clean = plainSuivi(next);
    live.current = clean;
    setBoard(clean);
    void enqueueBoardWrite(async () => {
      if (hold.current) return;
      let file = clean;
      try {
        const disk = parseBoard(await readCoffreText(path, ownerId));
        file = plainSuivi({
          ...clean,
          background: disk.background,
          layout: disk.layout,
        });
        live.current = file;
        setBoard(file);
      } catch {
        file = clean;
      }
      if (hold.current) return;
      try {
        await saveCoffreText(path, renderBoard(file), ownerId);
      } catch (reason: unknown) {
        setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
      }
    });
  }

  function addCard() {
    if (!board) return;
    const title = draft.trim();
    if (!title) return;
    const column = board.columns[0];
    if (!column) return;
    paint({ ...board, cards: [...board.cards, blankCard(column.id, title)] });
    setDraft("");
  }

  if (!board) {
    return <section className="crm-card rounded-2xl border border-slate-200 p-4 text-sm text-slate-500">{error || "Suivi…"}</section>;
  }

  return (
    <section className="crm-card rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
      <div className="crm-head flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <input
          value={board.subtitle}
          aria-label="Nom du suivi"
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold"
          onChange={(event) => {
            const current = live.current;
            if (!current) return;
            const next = { ...current, subtitle: event.target.value };
            live.current = next;
            setBoard(next);
          }}
          onBlur={() => { if (live.current) paint(live.current); }}
        />
        <div className="crm-pill flex rounded-full p-0.5 text-xs font-semibold">
          {(["kanban", "liste"] as const).map((mode) => (
            <button key={mode} type="button" className={view === mode ? "crm-fill rounded-full px-3 py-1 text-white" : "rounded-full px-3 py-1"} onClick={() => { setView(mode); localStorage.setItem(`crm-interne-view-${fiche.id}`, mode); }}>{mode === "kanban" ? "Kanban" : "Liste"}</button>
          ))}
        </div>
      </div>
      <div className="p-4">
        <form className="mb-3 flex gap-2" onSubmit={(event) => { event.preventDefault(); addCard(); }}>
          <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Nouvelle tâche" className="crm-field min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm" />
          <button type="submit" className="crm-fill shrink-0 rounded-full px-4 py-2 text-sm font-semibold text-white" disabled={draft.trim() === ""}>Ajouter</button>
        </form>
        {error ? <p className="mb-2 text-sm text-rose-700">{error}</p> : null}
        {view === "liste" ? (
          <ul className="space-y-2">
            {board.cards.map((card) => (
              <PipeRow key={card.id} fiche={fiche} card={card} board={board} onChange={paint} onMove={(columnId) => paint({ ...board, cards: board.cards.map((item) => item.id === card.id ? { ...item, columnId } : item) })} />
            ))}
          </ul>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {board.columns.map((column, index) => (
              <div key={column.id} className="crm-pipe-col w-60 shrink-0 rounded-xl p-2">
                <input
                  value={column.title}
                  aria-label="Nom de la colonne"
                  className="mb-2 w-full bg-transparent text-xs font-semibold uppercase tracking-wide"
                  onChange={(event) => {
                    const current = live.current;
                    if (!current) return;
                    const next = { ...current, columns: current.columns.map((item) => item.id === column.id ? { ...item, title: event.target.value } : item) };
                    live.current = next;
                    setBoard(next);
                  }}
                  onBlur={() => { if (live.current) paint(live.current); }}
                />
                <div className="space-y-2">
                  {board.cards.filter((card) => card.columnId === column.id).map((card) => (
                    <PipeCard key={card.id} fiche={fiche} card={card} board={board} next={board.columns[index + 1]?.id || ""} nextTitle={board.columns[index + 1]?.title || ""} onChange={paint} onMove={() => paint({ ...board, cards: board.cards.map((item) => item.id === card.id ? { ...item, columnId: board.columns[index + 1]?.id || item.columnId } : item) })} />
                  ))}
                </div>
              </div>
            ))}
            <button type="button" className="h-10 shrink-0 rounded-full border border-slate-200 px-3 text-xs font-semibold" onClick={() => paint({ ...board, columns: [...board.columns, { id: freshId("col"), title: "Colonne" }] })}>+ Colonne</button>
          </div>
        )}
      </div>
    </section>
  );
}

function PipeCard({ fiche, card, board, next, nextTitle, onChange, onMove }: { fiche: CrmFiche; card: BoardFile["cards"][number]; board: BoardFile; next: string; nextTitle: string; onChange: (board: BoardFile) => void; onMove: () => void }) {
  return (
    <article className="crm-pipe-card rounded-xl p-2">
      <p className="text-sm font-medium">{card.title}</p>
      <div className="mt-2 flex flex-wrap gap-1">
        {next ? <button type="button" className="crm-soft rounded-full px-2 py-1 text-[11px] font-semibold" onClick={onMove}>→ {nextTitle}</button> : null}
        <SendButton fiche={fiche} title={card.title} />
        <button type="button" className="rounded-full px-2 py-1 text-[11px] font-semibold text-rose-600" onClick={() => onChange({ ...board, cards: board.cards.filter((item) => item.id !== card.id) })}>Retirer</button>
      </div>
    </article>
  );
}

function PipeRow({ fiche, card, board, onChange, onMove }: { fiche: CrmFiche; card: BoardFile["cards"][number]; board: BoardFile; onChange: (board: BoardFile) => void; onMove: (columnId: string) => void }) {
  return (
    <li className="crm-pipe-card flex flex-wrap items-center gap-2 rounded-xl px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{card.title}</span>
      <select className="crm-field rounded-full border px-2 py-1 text-xs" value={card.columnId} onChange={(event) => onMove(event.target.value)}>
        {board.columns.map((column) => <option key={column.id} value={column.id}>{column.title}</option>)}
      </select>
      <SendButton fiche={fiche} title={card.title} />
      <button type="button" className="text-xs font-semibold text-rose-600" onClick={() => onChange({ ...board, cards: board.cards.filter((item) => item.id !== card.id) })}>Retirer</button>
    </li>
  );
}

function SendButton({ fiche, title }: { fiche: CrmFiche; title: string }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(todayStamp);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  if (!open) {
    return <button type="button" className="rounded-full px-2 py-1 text-[11px] font-semibold" onClick={() => { setOpen(true); setNote(""); }}>Semaine</button>;
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span className="w-36"><DateField value={day} onChange={setDay} /></span>
      <button
        type="button"
        className="crm-fill rounded-full px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setNote("");
          void createTask({ title, day, allDay: true, fiche: { id: fiche.id, name: fiche.name }, board: boardName(fiche) })
            .then(() => { setNote("Envoyée"); setOpen(false); })
            .catch((reason: unknown) => setNote(reason instanceof Error ? reason.message : "Envoi impossible"))
            .finally(() => setBusy(false));
        }}
      >{busy ? "…" : "Envoyer"}</button>
      {note ? <span className="text-[11px] text-slate-500">{note}</span> : null}
    </span>
  );
}

function AppDoor({ title, detail, href }: { title: string; detail: string; href: string }) {
  return (
    <section className="crm-card overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
      <h2 className="crm-head flex items-center gap-2 border-b px-4 py-2 text-sm font-semibold">
        <span>{title}</span>
        <a className="ml-auto text-xs font-semibold underline" href={href} target="_blank" rel="noopener noreferrer">Nouvel onglet</a>
      </h2>
      <p className="px-4 pt-3 text-sm text-slate-500">{detail}</p>
      <iframe title={title} src={href} className="mt-3 h-80 w-full border-0" />
    </section>
  );
}

function freshPipe(): BoardFile {
  const board = emptyBoard();
  return {
    ...board,
    subtitle: "Suivi",
    source: "local",
    mainAll: false,
    columns: [
      { id: freshId("col"), title: "À faire" },
      { id: freshId("col"), title: "En cours" },
      { id: freshId("col"), title: "Fait" },
    ],
  };
}

function boardName(fiche: CrmFiche): string {
  return fiche.name;
}

function dayTitle(stamp: string): string {
  const [year, month, day] = stamp.split("-").map(Number);
  if (!year || !month || !day) return "";
  return new Date(year, month - 1, day).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}
